import React, { useState, useRef } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { ReCAPTCHA } from 'react-google-recaptcha';
import {
  Box,
  Text,
  Link,
  useDisclosure,
  Center,
  HStack,
} from '@chakra-ui/react';
import { getCountryCallingCode } from 'libphonenumber-js/max'; // NOTE: use most strict validation (same as old checkout page)
import { isEmpty } from 'lodash-es';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import { ALL_SUPPORTED_COUNTRIES_STRING } from '../../../../../constants/userSetting';
import authenticationService from '../../../../../service/authenticationService';
import { PANEL } from '../../../constants/panel';
import { registerStatus } from '../../../constants/registerStatus';
import { useAppContext } from '../../../context/appContext';
import { checkIsRegistrationExisted } from '../../../utils/checkRegisterStatus';
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import {
  Input,
  MobilePhoneInput,
  TitleText,
  Button,
  Checkbox,
  ErrorText,
} from '../../../components';
import {
  RegisterStatusPopup,
  FacebookAccount,
  LineAccount,
} from '../../components';

// eslint-disable-next-line react/prop-types
const StepOne = ({ handleNextStep }) => {
  const {
    setCurrentPanel,
    setDefaultAccount,
    merchantData,
    isEmailRequired,
    isMobilePhoneRequired,
    slFeatureService,
    userSetting,
    grecaptcha,
    isCustomizedReCAPTCHAAvailable,
    isFacebookLoginEnabled,
    isLineLoginEnabled,
  } = useAppContext();
  const { t } = useTranslation();
  const {
    errors,
    control,
    handleSubmit,
    formState,
    trigger,
    setError,
    setValue,
  } = useFormContext();
  const [isCheckingRegisterStatus, setIsCheckingRegisterStatus] = useState(
    false,
  );
  const {
    isOpen: isOpenRegistrationStatusPopup,
    onOpen: openRegistrationStatusPopup,
    onClose: closeRegistrationStatusPopup,
  } = useDisclosure();
  const [registrationResult, setRegistrationResult] = useState('');
  const [registrationErrorMessage, setRegistrationErrorMessage] = useState('');
  const refReCAPTCHA = useRef();

  const { field: phoneCountryField } = useController({
    name: 'phone_country',
    control,
    defaultValue: merchantData.base_country_code,
  });

  const { field: mobilePhoneField } = useController({
    name: 'mobile_phone',
    control,
    defaultValue: '',
  });
  const { field: emailField } = useController({
    name: 'email',
    control,
    defaultValue: '',
  });
  const { field: subscriptionField } = useController({
    name: 'subscription',
    control,
    defaultValue: slFeatureService.hasFeature('pretick_accept_marketing'),
  });
  const { field: acceptPolicyField } = useController({
    name: 'accept_policy',
    control,
    defaultValue: false,
  });

  const btnDisabled =
    isEmpty(emailField.value) && isEmpty(mobilePhoneField.value);

  const applyReCAPTCHAToPayload = async (payload = {}) => {
    if (isCustomizedReCAPTCHAAvailable) {
      const token = await refReCAPTCHA.current?.executeAsync();
      refReCAPTCHA.current?.reset();
      if (token) {
        payload[RECAPTCHA_TOKEN_FIELD] = token;
      }
    }
    return payload;
  };

  const notifyUserOfGuestEmail = async (emailPayload) => {
    const payload = await applyReCAPTCHAToPayload({ user: emailPayload });
    const res = await authenticationService.createUser(payload);
    if (res?.data?.message) {
      const { message } = res?.data ?? {};
      setError('email', {
        message,
      });
    }
  };

  const notifyUserOfGuestMobilePhone = async (mobilePayload) => {
    const res = await authenticationService.registerValidation({
      user: mobilePayload,
    });
    if (res?.data?.message) {
      const { message } = res?.data ?? {};
      setError('mobile_phone', {
        message,
      });
    }
  };

  const onSubmit = async () => {
    if (!isEmpty(errors)) return;
    if (isMobilePhoneRequired) {
      mobilePhoneField.onChange(
        formatPhoneNumber({
          mobilePhone: mobilePhoneField.value,
          country: phoneCountryField.value,
        }),
      );
    }
    const email = emailField.value.trim();
    const mobilePayload = {
      mobile_phone: mobilePhoneField.value,
      country_calling_code: getCountryCallingCode(phoneCountryField.value),
    };
    let isUnmount = false;
    try {
      setIsCheckingRegisterStatus(true);
      const registerStatusResponse = await authenticationService.checkRegisterStatus(
        {
          ...(isEmailRequired ? { email } : {}),
          ...(isMobilePhoneRequired ? mobilePayload : {}),
        },
      );
      const result = checkIsRegistrationExisted(registerStatusResponse.data);
      setRegistrationResult(result);

      switch (result) {
        case registerStatus.ALLOW_CREATE_MEMBER: {
          isUnmount = true;
          handleNextStep();
          break;
        }
        case registerStatus.EMAIL_BELONGS_TO_EXISTING_GUEST: {
          await notifyUserOfGuestEmail({ email });
          break;
        }
        case registerStatus.EMAIL_BELONGS_TO_EXISTING_MEMBER: {
          setRegistrationErrorMessage(
            t('Register Status - result - email belongs to member'),
          );
          openRegistrationStatusPopup();
          break;
        }
        case registerStatus.MOBILE_PHONE_BELONGS_TO_EXISTING_GUEST: {
          await notifyUserOfGuestMobilePhone(mobilePayload);
          break;
        }
        case registerStatus.MOBILE_PHONE_BELONGS_TO_EXISTING_MEMBER: {
          setRegistrationErrorMessage(
            t('Register Status - result - mobile phone belongs to member'),
          );
          openRegistrationStatusPopup();
          break;
        }
        case registerStatus.EMAIL_AND_MOBILE_PHONE_BOTH_NOT_ALLOWED: {
          setRegistrationErrorMessage(
            t(
              'Register Status - result - email and mobile phone both not allowed',
            ),
          );
          openRegistrationStatusPopup();
          break;
        }
      }
    } catch (e) {
      // Handle Unexpected Error
      console.error(e);
    } finally {
      if (!isUnmount) {
        setIsCheckingRegisterStatus(false);
      }
    }
  };

  const clearAccountInput = () => {
    if (
      [
        registerStatus.EMAIL_BELONGS_TO_EXISTING_MEMBER,
        registerStatus.EMAIL_AND_MOBILE_PHONE_BOTH_NOT_ALLOWED,
      ].includes(registrationResult)
    ) {
      setValue('email', '', { shouldValidate: false });
    }

    if (
      [
        registerStatus.MOBILE_PHONE_BELONGS_TO_EXISTING_MEMBER,
        registerStatus.EMAIL_AND_MOBILE_PHONE_BOTH_NOT_ALLOWED,
      ].includes(registrationResult)
    ) {
      setValue('mobile_phone', '', {
        shouldValidate: false,
      });
    }
  };

  const setAccountToLogin = () => {
    if (
      registrationResult === registerStatus.EMAIL_BELONGS_TO_EXISTING_MEMBER
    ) {
      setDefaultAccount({ email: emailField.value, mobile_phone: '' });
      return;
    }
    if (
      registrationResult ===
      registerStatus.MOBILE_PHONE_BELONGS_TO_EXISTING_MEMBER
    ) {
      setDefaultAccount({ email: '', mobile_phone: mobilePhoneField.value });
      return;
    }
    if (
      registrationResult ===
      registerStatus.EMAIL_AND_MOBILE_PHONE_BOTH_NOT_ALLOWED
    ) {
      setDefaultAccount({ email: '', mobile_phone: '' });
      return;
    }
  };

  return (
    <>
      {isOpenRegistrationStatusPopup && (
        <RegisterStatusPopup
          errorMessage={registrationErrorMessage}
          handleRegisterClick={() => {
            clearAccountInput();
            closeRegistrationStatusPopup();
          }}
          handleLoginClick={() => {
            setAccountToLogin();
            setCurrentPanel(PANEL.LOGIN);
          }}
        />
      )}
      <Box mb="72px">
        <TitleText mb="30px">{t('Sign Up')}</TitleText>
        <form onSubmit={handleSubmit(onSubmit)}>
          {isMobilePhoneRequired && (
            <Box mb="10px">
              <MobilePhoneInput
                formatOnInit={false}
                value={mobilePhoneField.value}
                onSelectFlag={(_number, selectedCountryData) => {
                  phoneCountryField.onChange(
                    selectedCountryData?.iso2?.toUpperCase(),
                  );
                  if (formState.isSubmitted) {
                    trigger('mobile_phone');
                  }
                }}
                onPhoneNumberBlur={(_isValid, newNumber) => {
                  if (
                    newNumber.length === 0 ||
                    !isEmpty(errors?.mobile_phone?.message) ||
                    !formState.isSubmitted
                  ) {
                    return;
                  }
                  mobilePhoneField.onChange(
                    formatPhoneNumber({
                      mobilePhone: newNumber,
                      country: phoneCountryField.value.toUpperCase(),
                    }),
                  );
                }}
                onPhoneNumberChange={(_isValid, newNumber) => {
                  mobilePhoneField.onChange(newNumber.replace(/\+/, ''));
                }}
                label={t('Mobile Number')}
                defaultCountry={phoneCountryField.value}
                isInvalid={!isEmpty(errors?.mobile_phone?.message)}
                errorMessage={t(errors?.mobile_phone?.message)}
                onlyCountries={
                  userSetting?.sms_verification?.supported_countries.includes(
                    ALL_SUPPORTED_COUNTRIES_STRING,
                  )
                    ? []
                    : userSetting?.sms_verification?.supported_countries.map(
                        (country) => country.toLowerCase(),
                      ) || []
                }
              />
            </Box>
          )}
          {isEmailRequired && (
            <Box mb="32px">
              <Input
                onChange={(e) => emailField.onChange(e.target.value)}
                value={emailField.value}
                label={t('Email')}
                placeholder={t('Enter your email')}
                isInvalid={!isEmpty(errors?.email?.message)}
                errorMessage={t(errors?.email?.message)}
              />
            </Box>
          )}

          <Box mb="12px">
            <Checkbox
              isChecked={subscriptionField.value}
              onChange={(e) => subscriptionField.onChange(e.target.checked)}
            >
              {t('Accept to receive store updates', {
                shopName: merchantData.name,
              })}
            </Checkbox>
          </Box>
          <Box>
            <Checkbox
              isChecked={acceptPolicyField.value}
              onChange={(e) => acceptPolicyField.onChange(e.target.checked)}
            >
              <Trans i18nKey="Terms of Use and Privacy Policy">
                <Link
                  color="#272D36"
                  textDecoration="underline"
                  href="/about/terms"
                  target="_blank"
                  fontWeight={500}
                />
                and
                <Link
                  color="#272D36"
                  textDecoration="underline"
                  href="/about/privacy-policy"
                  target="_blank"
                  fontWeight={500}
                />
              </Trans>
            </Checkbox>
            {!isEmpty(errors?.accept_policy?.message) && (
              <ErrorText>{t(errors.accept_policy.message)}</ErrorText>
            )}
          </Box>

          <Button
            isFullWidth
            onClick={handleSubmit(onSubmit)}
            mt="32px"
            type="submit"
            isDisabled={btnDisabled}
            isLoading={isCheckingRegisterStatus}
          >
            {t('Next')}
          </Button>
          {isCustomizedReCAPTCHAAvailable && (
            <ReCAPTCHA
              ref={refReCAPTCHA}
              size="invisible"
              grecaptcha={grecaptcha}
              sitekey={userSetting.recaptcha_signup.site_key}
            />
          )}
        </form>
        {(isFacebookLoginEnabled || isLineLoginEnabled) && (
          <Box mt="24px">
            <Text
              fontSize="12px"
              fontWeight={500}
              color="#272D36"
              textAlign="center"
              mb="8px !important"
            >
              {t('sign up with social account')}
            </Text>
            <Center>
              <HStack spacing="12px">
                {isFacebookLoginEnabled && (
                  <FacebookAccount
                    currentPanel={PANEL.SIGN_UP}
                    isSubscribed={subscriptionField.value}
                  />
                )}
                {isLineLoginEnabled && <LineAccount />}
              </HStack>
            </Center>
          </Box>
        )}
      </Box>
      <TitleText mb="24px">{t('Already a member')}</TitleText>
      <Text mb="24px">{t('Sign in to enjoy more benefits')}</Text>
      <Button
        isFullWidth
        onClick={() => setCurrentPanel(PANEL.LOGIN)}
        variant="outline"
      >
        {t('Sign In')}
      </Button>
    </>
  );
};

export default StepOne;
